import { CommonModule } from '@angular/common';
import { Component, HostBinding } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EpcotConfig, MyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { SportsTheme, injectCurrentTheme } from '@frontend/sports/common/ui/shared';
import { LinkDirective } from '@frontend/sports/common/ui/sports-themed-pills';
import { SelectedTabItem, SportsThemedTabsDirective, ThemeSize } from '@frontend/sports/common/ui/sports-themed-tab-group';
import { AdaptiveLayoutService } from '@frontend/sports/layout/feature';
import { TrackNavArea } from '@frontend/sports/navigation/core/feature/models';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { DsButton } from '@frontend/ui/button';
import { DsDivider } from '@frontend/ui/divider';
import { DsNotificationBubble } from '@frontend/ui/notification-bubble';
import { DsSegmentedControlModule } from '@frontend/ui/segmented-control';
import { DsTabsModule, TabsGroupSizesType } from '@frontend/ui/tabsgroup';
import { DeviceService } from '@frontend/vanilla/core';
import { SpeculativeLinkDirective } from '@frontend/vanilla/features/speculative-link';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { Observable, distinctUntilChanged, map } from 'rxjs';

import { NavigationService } from '../navigation/navigation.service';
import { MyBetsSubNavigationComponent } from './mybets-sub-navigation/my-bets-sub-navigation.component';
import { SubNavigation } from './sport-sub-navigation/sport-sub-navigation.service';
import { SubNavigationItem } from './sub-navigation.models';
import { SubNavigationService } from './sub-navigation.service';

@Component({
    selector: 'ms-sub-navigation',
    templateUrl: 'sub-navigation.component.html',
    styleUrls: ['sub-navigation.component.scss'],
    imports: [
        DsTabsModule,
        DsNotificationBubble,
        DsButton,
        IconCustomComponent,
        SpeculativeLinkDirective,
        LinkDirective,
        DsDivider,
        CommonModule,
        SportsThemedTabsDirective,
        DsSegmentedControlModule,
        MyBetsSubNavigationComponent,
    ],
})
export class SubNavigationComponent {
    tabs: SubNavigationItem[] = [];
    selectedTab: SubNavigationItem;
    readonly currentTheme = injectCurrentTheme();
    readonly inverseThemes: SportsTheme[] = ['mgm-4', 'giocodigitale'];
    readonly excludedDividerThemes: SportsTheme[] = ['mgm-4', 'sports-interaction', 'party-poker', 'party-casino'];
    readonly tabInverse: boolean;
    @HostBinding('class.epcot-enabled') readonly isEpcotEnabled: boolean = this.epcotConfig.isEnabled;
    readonly showDivider: boolean;

    subNavigation = SubNavigation;
    tabSize: TabsGroupSizesType;
    isLiveButtonEnabled: boolean;
    isLargeTab: boolean;
    isFullWidth: boolean;
    themedTabSize: ThemeSize;
    isMyBetsTabRedesignEnabled = this.myBetsConfig.isMyBetTabRedesignEnabled;
    isMyBetsRoute$: Observable<boolean>;

    constructor(
        private subnavigation: SubNavigationService,
        public adaptiveLayout: AdaptiveLayoutService,
        private urlHelperService: UrlHelperService,
        private navigationService: NavigationService,
        private redirectHelperService: RedirectHelperService,
        private deviceService: DeviceService,
        private myBetsConfig: MyBetsConfig,
        private routerEventsService: RouterEventsService,
        private epcotConfig: EpcotConfig,
    ) {
        this.subnavigation.tabs$.pipe(takeUntilDestroyed()).subscribe(this.tabsChanged);
        this.tabInverse = this.inverseThemes.includes(this.currentTheme);
        this.showDivider = !this.excludedDividerThemes.includes(this.currentTheme);
        this.subnavigation.tabSize$.pipe(takeUntilDestroyed()).subscribe((tabSize) => {
            this.tabSize = tabSize;
            this.isLargeTab = tabSize === 'large';
        });
        this.subnavigation.fullWidth$.subscribe((fullWidth) => {
            this.isFullWidth = fullWidth;
        });
        this.themedTabSize = {
            'betboo-br': 'small',
            'sportingbet-br': 'small',
        };
        this.isMyBetsRoute$ = this.routerEventsService.currentActivationEnd.pipe(
            map((activationEnd) => activationEnd?.snapshot.data['tag'] === RouteTag.MyBets),
            distinctUntilChanged(),
        );
    }

    get isMobilePhone(): boolean {
        return this.deviceService.isMobilePhone;
    }

    get active(): SubNavigationItem {
        return this.tabs.find((tab) => tab.active) ?? this.tabs[0];
    }

    get liveInSportTab(): SubNavigationItem | undefined {
        return this.tabs.find((tab) => tab.id === this.subNavigation.LiveInSportPage);
    }

    click(tab: SubNavigationItem): void {
        this.clickEvent(tab);
    }

    selected(selectedTab: SelectedTabItem): void {
        this.tabs = this.tabs.map((item) => {
            item.active = item.id.toString() === selectedTab.name;

            if (item.active) {
                this.selectedTab = item;
            }
            return {
                ...item,
            };
        });
        this.clickEvent(this.selectedTab);
    }

    clickEvent(tab: SubNavigationItem): void {
        if (tab.url) {
            this.redirectHelperService.goToPage(tab.url, tab.ignoreBrowserHistory);

            const url = this.urlHelperService.getLastSegment(tab.url);
            this.navigationService.tracking(url, TrackNavArea.NavSub);
        }
    }

    private tabsChanged = (newTabs: SubNavigationItem[]): void => {
        this.tabs.length = 0;
        this.tabs.push(...newTabs);
        this.isLiveButtonEnabled = !!this.liveInSportTab;
    };
}
